import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import EmbedPreview from '@/components/feature/EmbedPreview';
import PostulerModal from '@/pages/missions/components/PostulerModal';
import PublicationSubmitModal, {
  readPublications,
} from '@/pages/client/dashboard/missions/components/PublicationSubmitModal';
import {
  CANDIDATURE_STATUT_META,
  MISSION_PLATFORMS,
  MISSION_STATUT_META,
  computeMissionPayout,
  fetchMissionCandidatures,
  fetchMissions,
  postulerMission,
  type Mission,
  type MissionCandidature,
} from '@/lib/missions';

// ─────────────────────────────────────────────────────────────
// Espace client — détail d'une mission (/mon-compte/missions/:id).
//
// Le créateur consulte le brief complet, suit sa candidature, et
// dépose ses liens de publication (une ligne par réseau, bouton « + »).
// L'aperçu du lien s'ouvre dans le mini-navigateur intégré.
// ─────────────────────────────────────────────────────────────

function daysLeft(dateLimite: string | null): number | null {
  if (!dateLimite) return null;
  const time = new Date(dateLimite).getTime();
  if (Number.isNaN(time)) return null;
  return Math.ceil((time - Date.now()) / 86400000);
}

function fmtMoney(value: number | null | undefined, devise: string): string {
  const n = Number(value) || 0;
  return `${n.toLocaleString('fr-FR', { maximumFractionDigits: 2 })} ${devise}`;
}

export default function ClientMissionDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const missionId = Number(id);
  const myId = Number(user?.id || 0);

  const [mission, setMission] = useState<Mission | null>(null);
  const [candidatures, setCandidatures] = useState<MissionCandidature[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [toast, setToast] = useState('');
  const [applyOpen, setApplyOpen] = useState(false);
  const [submitOpen, setSubmitOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState('');
  const [previewInput, setPreviewInput] = useState('');

  const montrerToast = (text: string) => {
    setToast(text);
    window.setTimeout(() => setToast(''), 4000);
  };

  const load = useCallback(async () => {
    if (!Number.isFinite(missionId)) {
      setError('Mission introuvable.');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const all = await fetchMissions();
      const found = all.find((m) => m.id === missionId) || null;
      setMission(found);
      if (found) {
        setCandidatures(await fetchMissionCandidatures([found.id]));
        const first = found.lien_contenu || '';
        setPreviewUrl(first);
        setPreviewInput(first);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Impossible de charger cette mission.');
    } finally {
      setLoading(false);
    }
  }, [missionId]);

  useEffect(() => { void load(); }, [load]);

  const myCandidature = useMemo(
    () => candidatures.find((c) => c.user_id === myId) || null,
    [candidatures, myId],
  );
  const isOwner = !!mission && Number(mission.user_id) === myId;
  const publications = readPublications(myCandidature);

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i>
      </div>
    );
  }

  if (error || !mission) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center px-4 text-center">
        <div className="w-14 h-14 rounded-2xl bg-background-100 flex items-center justify-center mb-4">
          <i className="ri-megaphone-line text-2xl text-foreground-400"></i>
        </div>
        <h1 className="text-lg font-bold font-heading text-foreground-950">Mission introuvable</h1>
        <p className="mt-1 text-sm text-foreground-500">{error || "Cette mission n'existe plus."}</p>
        <Link
          to="/mon-compte/missions"
          className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold no-underline hover:bg-primary-600"
        >
          <i className="ri-arrow-left-line"></i>Retour aux missions
        </Link>
      </div>
    );
  }

  const devise = mission.monaie || 'MAD';
  const statut = MISSION_STATUT_META[mission.statut] || { label: mission.statut, className: 'bg-background-200/70 text-foreground-600' };
  const candMeta = myCandidature
    ? CANDIDATURE_STATUT_META[myCandidature.statut] || { label: myCandidature.statut, className: 'bg-background-200/70 text-foreground-600' }
    : null;
  const left = daysLeft(mission.date_limite);
  const canSubmit = !!myCandidature && ['acceptee', 'livree', 'rejetee', 'validee'].includes(myCandidature.statut);
  const gains = myCandidature ? computeMissionPayout(mission.tarif_1000_vues, myCandidature.vues_validees) : 0;

  return (
    <div className="min-h-screen bg-background-50">
      <div className="max-w-6xl mx-auto px-4 md:px-6 py-6 md:py-10">
        {/* Fil d'ariane */}
        <div className="flex items-center gap-2 text-xs text-foreground-500 mb-5 flex-wrap">
          <Link to="/mon-compte/missions" className="inline-flex items-center gap-1 hover:text-foreground-900 no-underline">
            <i className="ri-arrow-left-line"></i>Mes missions
          </Link>
          <span className="text-foreground-300">/</span>
          <span className="text-foreground-700 font-medium truncate max-w-[280px]">{mission.titre}</span>
        </div>

        {/* En-tête */}
        <header className="rounded-xl border border-background-200/70 bg-background-50 p-4 md:p-6">
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <span className={`px-2.5 py-1 rounded-full text-[11px] font-semibold ${statut.className}`}>{statut.label}</span>
            {candMeta && (
              <span className={`px-2.5 py-1 rounded-full text-[11px] font-semibold ${candMeta.className}`}>{candMeta.label}</span>
            )}
            {left != null && (
              <span className={`px-2.5 py-1 rounded-full text-[11px] font-semibold ${
                left < 0 ? 'bg-red-50 text-red-600' : left <= 3 ? 'bg-amber-50 text-amber-700' : 'bg-secondary-100 text-secondary-900'
              }`}>
                <i className="ri-time-line mr-1"></i>
                {left < 0 ? 'Échéance dépassée' : `${left} jour${left > 1 ? 's' : ''} restant${left > 1 ? 's' : ''}`}
              </span>
            )}
            <span className="px-2.5 py-1 rounded-full text-[11px] font-medium bg-background-100 text-foreground-600">
              Durée max 31 jours
            </span>
          </div>
          <h1 className="text-xl md:text-3xl font-bold font-heading text-foreground-950 leading-tight">{mission.titre}</h1>
          <p className="mt-2 text-xs md:text-sm text-foreground-500 flex items-center gap-x-4 gap-y-1 flex-wrap">
            <span><i className="ri-store-2-line mr-1"></i>{mission.auteur_nom || 'Un commerce'}</span>
            {(mission.ville || mission.pays) && (
              <span><i className="ri-map-pin-line mr-1"></i>{[mission.ville, mission.pays].filter(Boolean).join(', ')}</span>
            )}
            <span className="font-bold text-primary-700">{fmtMoney(mission.tarif_1000_vues, devise)} / 1000 vues</span>
          </p>

          <div className="flex flex-wrap items-center gap-1.5 mt-3">
            {(mission.plateformes || []).map((key) => {
              const meta = MISSION_PLATFORMS.find((p) => p.key === key);
              return (
                <span key={key} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-900 text-[11px] font-medium whitespace-nowrap">
                  <i className={meta?.icon || 'ri-global-line'}></i>{meta?.label || key}
                </span>
              );
            })}
          </div>
        </header>

        {isOwner && (
          <div className="mt-4 flex items-start gap-2 px-4 py-3 rounded-lg bg-secondary-50 border border-secondary-200/70 text-xs text-secondary-900">
            <i className="ri-information-line mt-0.5"></i>
            <span>
              Vous êtes le propriétaire de cette campagne.{' '}
              <Link to={`/dashboard/missions/${mission.id}`} className="underline font-semibold whitespace-nowrap">
                Gérer les candidatures
              </Link>
            </span>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-[1.5fr_1fr] gap-4 mt-4">
          <div className="space-y-4 min-w-0">
            {/* Brief */}
            <section className="rounded-xl border border-background-200/70 bg-background-50 p-4 md:p-5">
              <h2 className="text-sm font-bold font-heading text-foreground-950 mb-2 flex items-center gap-2">
                <i className="ri-file-text-line text-primary-500"></i>Ce que le commerce attend
              </h2>
              <p className="text-sm text-foreground-700 leading-relaxed whitespace-pre-line">
                {mission.description || 'Aucune description fournie.'}
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 mt-4">
                <div className="rounded-lg bg-background-100 p-2.5">
                  <p className="text-[10px] uppercase tracking-wide text-foreground-400 font-semibold">Type</p>
                  <p className="text-xs font-semibold text-foreground-800 mt-0.5">{mission.type_contenu || '—'}</p>
                </div>
                <div className="rounded-lg bg-background-100 p-2.5">
                  <p className="text-[10px] uppercase tracking-wide text-foreground-400 font-semibold">Vues visées</p>
                  <p className="text-xs font-semibold text-foreground-800 mt-0.5">{(Number(mission.vues_cibles) || 0).toLocaleString('fr-FR')}</p>
                </div>
                <div className="rounded-lg bg-background-100 p-2.5">
                  <p className="text-[10px] uppercase tracking-wide text-foreground-400 font-semibold">Musique</p>
                  <p className="text-xs font-semibold text-foreground-800 mt-0.5">{mission.musique_autorisee ? 'Autorisée' : 'Non'}</p>
                </div>
                <div className="rounded-lg bg-background-100 p-2.5">
                  <p className="text-[10px] uppercase tracking-wide text-foreground-400 font-semibold">Échéance</p>
                  <p className="text-xs font-semibold text-foreground-800 mt-0.5">
                    {mission.date_limite ? new Date(mission.date_limite).toLocaleDateString('fr-FR') : '—'}
                  </p>
                </div>
              </div>
              {mission.lien_contenu && (
                <button
                  onClick={() => { setPreviewUrl(mission.lien_contenu || ''); setPreviewInput(mission.lien_contenu || ''); }}
                  className="mt-4 inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full bg-background-100 text-foreground-700 text-xs font-semibold cursor-pointer hover:bg-background-200/70 transition-colors"
                >
                  <i className="ri-window-line"></i>Voir le contenu à partager
                </button>
              )}
            </section>

            {/* Candidature / participation */}
            <section className="rounded-xl border border-background-200/70 bg-background-50 p-4 md:p-5">
              <h2 className="text-sm font-bold font-heading text-foreground-950 mb-3 flex items-center gap-2">
                <i className="ri-send-plane-line text-primary-500"></i>
                {myCandidature ? 'Ma participation' : 'Postuler'}
              </h2>

              {!myCandidature && (
                <div className="space-y-3">
                  <p className="text-xs text-foreground-600 leading-relaxed">
                    Envoyez vos liens de profils et votre audience. Le commerce vous notifie si vous êtes retenu,
                    puis vous publiez et déposez vos liens ici même.
                  </p>
                  <button
                    onClick={() => setApplyOpen(true)}
                    disabled={mission.statut !== 'ouverte'}
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap cursor-pointer hover:bg-primary-600 disabled:opacity-50 transition-colors"
                  >
                    <i className="ri-user-add-line"></i>
                    {mission.statut === 'ouverte' ? 'Postuler à cette mission' : 'Candidatures fermées'}
                  </button>
                </div>
              )}

              {myCandidature && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
                    <div className="rounded-lg bg-background-100 p-3">
                      <p className="text-[10px] uppercase tracking-wide text-foreground-400 font-semibold">Vues déclarées</p>
                      <p className="text-sm font-bold text-foreground-950">{(Number(myCandidature.vues_declarees) || 0).toLocaleString('fr-FR')}</p>
                    </div>
                    <div className="rounded-lg bg-accent-50 p-3">
                      <p className="text-[10px] uppercase tracking-wide text-accent-800 font-semibold">Vues validées</p>
                      <p className="text-sm font-bold text-foreground-950">{(Number(myCandidature.vues_validees) || 0).toLocaleString('fr-FR')}</p>
                    </div>
                    <div className="rounded-lg bg-secondary-50 p-3">
                      <p className="text-[10px] uppercase tracking-wide text-secondary-900 font-semibold">Montant</p>
                      <p className="text-sm font-bold text-foreground-950">{fmtMoney(gains || Number(myCandidature.montant_du) || 0, devise)}</p>
                    </div>
                  </div>

                  {publications.length > 0 ? (
                    <div>
                      <p className="text-xs font-semibold text-foreground-600 mb-2">Mes publications envoyées</p>
                      <div className="space-y-2">
                        {publications.map((p) => {
                          const meta = MISSION_PLATFORMS.find((x) => x.key === p.platform);
                          return (
                            <div key={`${p.platform}-${p.url}`} className="flex items-center gap-2 rounded-lg border border-background-200/70 bg-background-100 px-3 py-2">
                              <span className="w-8 h-8 rounded-full bg-background-50 text-foreground-700 flex items-center justify-center flex-shrink-0">
                                <i className={meta?.icon || 'ri-link'}></i>
                              </span>
                              <div className="min-w-0 flex-1">
                                <p className="text-[11px] font-semibold text-foreground-800">{meta?.label || p.platform}</p>
                                <p className="text-[10px] text-foreground-500 truncate">{p.url}</p>
                              </div>
                              <button
                                onClick={() => { setPreviewUrl(p.url); setPreviewInput(p.url); }}
                                className="w-8 h-8 rounded-full bg-background-50 text-primary-600 flex items-center justify-center flex-shrink-0 cursor-pointer hover:bg-primary-50"
                                title="Aperçu"
                              >
                                <i className="ri-eye-line"></i>
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ) : (
                    <p className="text-xs text-foreground-500 leading-relaxed">
                      Aucun lien de publication envoyé pour le moment.
                      {canSubmit ? ' Publiez le contenu puis déposez vos liens ci-dessous.' : ' Vous pourrez ajouter vos liens dès que vous serez retenu.'}
                    </p>
                  )}

                  <button
                    onClick={() => setSubmitOpen(true)}
                    disabled={!canSubmit}
                    className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full bg-accent-500 text-background-50 text-sm font-semibold whitespace-nowrap cursor-pointer hover:bg-accent-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <i className="ri-add-circle-line"></i>
                    {publications.length > 0 ? 'Mettre à jour mes liens' : 'Partager mes liens de publication'}
                  </button>
                  {!canSubmit && (
                    <p className="text-[11px] text-foreground-400">
                      Attendez d&apos;être retenu par le commerce pour déposer vos liens.
                    </p>
                  )}
                </div>
              )}
            </section>
          </div>

          {/* Mini-navigateur */}
          <aside className="rounded-xl border border-background-200/70 bg-background-50 overflow-hidden h-fit lg:sticky lg:top-20">
            <div className="flex items-center gap-1.5 px-3 py-2 border-b border-background-200/70 bg-background-100">
              <span className="w-2 h-2 rounded-full bg-red-400 flex-shrink-0"></span>
              <span className="w-2 h-2 rounded-full bg-amber-400 flex-shrink-0"></span>
              <span className="w-2 h-2 rounded-full bg-accent-500 flex-shrink-0"></span>
              <span className="text-[10px] font-semibold text-foreground-500 uppercase tracking-wide ml-1.5">Aperçu</span>
            </div>

            <div className="flex items-center gap-1.5 px-2.5 py-2 border-b border-background-200/70">
              <i className="ri-lock-line text-xs text-foreground-400 flex-shrink-0"></i>
              <input
                value={previewInput}
                onChange={(e) => setPreviewInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') setPreviewUrl(previewInput.trim()); }}
                placeholder="Collez un lien à prévisualiser…"
                className="flex-1 min-w-0 px-2 py-1.5 rounded-md bg-background-100 border border-background-200/70 text-[11px] text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300"
              />
              <button
                onClick={() => setPreviewUrl(previewInput.trim())}
                className="w-7 h-7 rounded-md bg-primary-500 text-background-50 flex items-center justify-center flex-shrink-0 cursor-pointer hover:bg-primary-600 transition-colors"
                title="Prévisualiser"
              >
                <i className="ri-arrow-right-line text-xs"></i>
              </button>
            </div>

            <div className="aspect-[4/3] bg-background-100">
              {previewUrl ? (
                <EmbedPreview url={previewUrl} title="Aperçu de la publication" />
              ) : (
                <div className="w-full h-full flex flex-col items-center justify-center px-6 text-center">
                  <i className="ri-window-line text-2xl text-foreground-300 mb-1"></i>
                  <p className="text-[11px] text-foreground-400">
                    Le contenu de la mission et vos publications s&apos;affichent ici.
                  </p>
                </div>
              )}
            </div>

            {previewUrl && (
              <div className="px-3 py-2 border-t border-background-200/70 flex items-center justify-between gap-2">
                <span className="text-[10px] text-foreground-400 truncate">{previewUrl}</span>
                <a
                  href={previewUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[10px] font-semibold text-primary-600 hover:text-primary-700 whitespace-nowrap no-underline"
                >
                  Nouvel onglet <i className="ri-external-link-line"></i>
                </a>
              </div>
            )}
          </aside>
        </div>
      </div>

      {applyOpen && (
        <PostulerModal
          mission={mission}
          profile={user || {}}
          onClose={() => setApplyOpen(false)}
          onSubmit={async (input) => {
            await postulerMission(
              mission,
              { id: myId, nom: user?.nomcommerce || user?.name || user?.email || 'Membre', email: user?.email || null },
              input,
            );
            setApplyOpen(false);
            montrerToast('Candidature envoyée. Suivez son statut sur cette page.');
            await load();
          }}
        />
      )}

      {submitOpen && myCandidature && (
        <PublicationSubmitModal
          mission={mission}
          candidature={myCandidature}
          onClose={() => setSubmitOpen(false)}
          onSubmitted={() => { void load(); montrerToast('Publications envoyées pour vérification.'); }}
        />
      )}

      {toast && (
        <div className="fixed bottom-20 left-1/2 -translate-x-1/2 z-[80] w-[calc(100vw-2rem)] max-w-md">
          <div className="flex items-start gap-2 px-4 py-3 rounded-lg bg-foreground-900 text-background-50 text-xs font-medium">
            <i className="ri-information-line mt-0.5"></i>
            <span className="flex-1">{toast}</span>
          </div>
        </div>
      )}
    </div>
  );
}